import { InsiderTermsAndConditionsStatus } from "@faire/web-api/indigofair/insider/InsiderTermsAndConditionsStatus";

import { useInsiderMembershipInformation } from "./useInsiderMembershipInformation";

interface IUseInsiderTermsAndConditionsConsentStatus {
  /** The status of the user's consent for the latest Insider Terms and Conditions. */
  insiderTermsAndConditionsConsentStatus:
    | keyof typeof InsiderTermsAndConditionsStatus
    | undefined;

  /** Indicates if the user's consent for the latest Insider Terms and Conditions is in a pending state. */
  isInsiderTermsAndConditionsPending: boolean;

  /** Indicates if the user's consent for the latest Insider Terms and Conditions is outdated. */
  isInsiderTermsAndConditionsOutdated: boolean;

  /** Indicates if the current retailer hasn't yet accepted the latest Insider terms and conditions. */
  isInsiderTermsAndConditionsPendingOrOutdated: boolean;

  /** Indicates if the user has accepted the latest Insider Terms and Conditions. */
  isInsiderTermsAndCondtionsAccepted: boolean;
}

/**
 * Custom hook to determine the consent status for Insider Terms and Conditions.
 *
 * @example
 * const { isInsiderTermsAndConditionsPendingOrOutdated } = useInsiderTermsAndConditionsConsentStatus();
 * if (isInsiderTermsAndConditionsPendingOrOutdated) {
 *   // Prompt the user to accept the latest Insider terms and conditions.
 * }
 */
export const useInsiderTermsAndConditionsConsentStatus =
  (): IUseInsiderTermsAndConditionsConsentStatus => {
    const { insiderMembershipInformation } = useInsiderMembershipInformation();

    const insiderTermsAndConditionsConsentStatus =
      insiderMembershipInformation?.terms_acceptance_status;
    const isInsiderTermsAndConditionsPending =
      insiderTermsAndConditionsConsentStatus ===
      InsiderTermsAndConditionsStatus.PENDING;
    const isInsiderTermsAndConditionsOutdated =
      insiderTermsAndConditionsConsentStatus ===
      InsiderTermsAndConditionsStatus.OUTDATED;
    const isInsiderTermsAndConditionsPendingOrOutdated =
      isInsiderTermsAndConditionsPending || isInsiderTermsAndConditionsOutdated;
    const isInsiderTermsAndCondtionsAccepted =
      insiderTermsAndConditionsConsentStatus ===
      InsiderTermsAndConditionsStatus.ACCEPTED;

    return {
      insiderTermsAndConditionsConsentStatus,
      isInsiderTermsAndConditionsPending,
      isInsiderTermsAndConditionsOutdated,
      isInsiderTermsAndConditionsPendingOrOutdated,
      isInsiderTermsAndCondtionsAccepted,
    };
  };
